import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Radar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";
import { homeSeo } from "@/i18n/seo";
import { posterSrc, prefersReducedMotion, isMobileViewport } from "@/cinematic/config";
import { scrollToAnchor } from "@/lib/smoothScroll";

gsap.registerPlugin(ScrollTrigger);

/**
 * A varredura do sonar — 4 fases controladas pela rolagem:
 * o pulso (hero), os ecos (descoberta), contatos hostis (vulnerabilidades)
 * e triangulação (análise por IA).
 * A rolagem troca as fases (crossfade) e revela os textos; dentro de cada
 * fase o vídeo do sonar toca sozinho em loop — leve e sempre fluido.
 */
const ScrollCinema = () => {
  const { lang } = useLanguage();
  const reduced = prefersReducedMotion();

  if (reduced) return <StaticStory key={lang} />;
  return <AnimatedStory key={lang} />;
};

const PHASES = 4;

const SECONDARY_CALL = {
  pt: "Proteja sua empresa antes que seja tarde",
  es: "Proteja su empresa antes de que sea tarde",
  en: "Protect your business before it's too late",
} as const;

import scene1Video from "../../../public/cinematic/scene-1.mp4.asset.json";
import scene1MobileVideo from "../../../public/cinematic/scene-1-mobile.mp4.asset.json";
import scene2Video from "../../../public/cinematic/scene-2.mp4.asset.json";
import scene2MobileVideo from "../../../public/cinematic/scene-2-mobile.mp4.asset.json";
import scene3Video from "../../../public/cinematic/scene-3.mp4.asset.json";
import scene3MobileVideo from "../../../public/cinematic/scene-3-mobile.mp4.asset.json";
import scene4Video from "../../../public/cinematic/scene-4.mp4.asset.json";
import scene4MobileVideo from "../../../public/cinematic/scene-4-mobile.mp4.asset.json";

const SCENE_VIDEOS: Array<{ desktop: string; mobile: string }> = [
  { desktop: scene1Video.url, mobile: scene1MobileVideo.url },
  { desktop: scene2Video.url, mobile: scene2MobileVideo.url },
  { desktop: scene3Video.url, mobile: scene3MobileVideo.url },
  { desktop: scene4Video.url, mobile: scene4MobileVideo.url },
];

const sceneSrc = (phase: number, mobile: boolean) => {
  const s = SCENE_VIDEOS[phase] ?? SCENE_VIDEOS[0];
  return mobile ? s.mobile : s.desktop;
};

/* ------------------------------------------------------------------ */
/* Conteúdo textual real (i18n) de cada cena                           */
/* ------------------------------------------------------------------ */

const useSceneCopy = () => {
  const { lang } = useLanguage();
  const f = translations.features;
  const hw = translations.howItWorks;

  return {
    easm: {
      title: t(f.items[0].title, lang),
      text: t(f.items[0].description, lang),
      chip: t(hw.asyncBadge, lang),
      severity: false,
    },
    vuln: {
      title: t(f.items[3].title, lang),
      text: t(f.items[3].description, lang),
      chip: "CVSS · KEV",
      severity: true,
    },
    ia: {
      title: t(f.items[7].title, lang),
      text: t(f.items[7].description, lang),
      chip: t(f.items[10].title, lang),
      severity: false,
    },
  };
};

/* ------------------------------------------------------------------ */
/* Versão animada (vídeo por fase + GSAP ScrollTrigger + Lenis)        */
/* ------------------------------------------------------------------ */

const AnimatedStory = () => {
  const { lang } = useLanguage();
  const h = translations.hero;
  const copy = useSceneCopy();
  const mobile = useRef(isMobileViewport()).current;
  const heroH1 = t(homeSeo.h1, lang);
  const secondaryCall = t(SECONDARY_CALL, lang);

  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const heroRef = useRef<HTMLDivElement>(null);
  const easmRef = useRef<HTMLDivElement>(null);
  const vulnRef = useRef<HTMLDivElement>(null);
  const iaRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState(0);
  const phaseRef = useRef(0);

  // a fase ativa toca UMA vez desde o início e congela no quadro final
  // (que é exatamente o primeiro quadro da fase seguinte); as demais pausam
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === phase) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [phase]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const ctx = gsap.context(() => {
      // A rolagem comanda a FASE (não os quadros)
      ScrollTrigger.create({
        trigger: wrap,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        onUpdate: (self) => {
          const next = Math.min(PHASES - 1, Math.floor(self.progress * PHASES));
          if (next !== phaseRef.current) {
            phaseRef.current = next;
            setPhase(next);
          }
        },
      });

      // Textos revelados nos momentos em que cada eco "responde"
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: wrap,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
        },
      });

      tl.to(hintRef.current, { opacity: 0, duration: 0.03 }, 0.04);
      tl.to(heroRef.current, { opacity: 0, y: -80, duration: 0.06 }, 0.2);

      tl.fromTo(easmRef.current, { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.05 }, 0.28);
      tl.to(easmRef.current, { opacity: 0, y: -70, duration: 0.05 }, 0.45);

      tl.fromTo(vulnRef.current, { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.05 }, 0.53);
      tl.to(vulnRef.current, { opacity: 0, y: -70, duration: 0.05 }, 0.7);

      tl.fromTo(iaRef.current, { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.05 }, 0.78);
      tl.to(iaRef.current, { opacity: 0, y: -70, duration: 0.05 }, 0.95);
    }, wrap);

    return () => {
      videoRefs.current.forEach((video) => video?.pause());
      ctx.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = [
    { value: "50+", label: t(h.stats.checks, lang) },
    { value: "24/7", label: t(h.stats.monitoring, lang) },
    { value: "0%", label: t(h.stats.impact, lang) },
    { value: "< 30min", label: t(h.stats.firstScan, lang) },
  ];

  return (
    <section id="hero" ref={wrapRef} className="relative z-10 h-[360vh]" aria-label="SentinelHub">
      <div className="dark sticky top-0 h-screen w-full overflow-hidden bg-[hsl(222,47%,6%)]">
        {/* uma camada de vídeo por fase, com crossfade */}
        {Array.from({ length: PHASES }).map((_, index) => (
          <video
            key={index}
            ref={(el) => (videoRefs.current[index] = el)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              phase === index ? "opacity-100" : "opacity-0"
            }`}
            src={sceneSrc(index, mobile)}
            poster={posterSrc(index + 1)}
            muted
            playsInline
            preload={index === 0 ? "auto" : "metadata"}
            width={mobile ? 960 : 1920}
            height={mobile ? 540 : 1080}
            aria-hidden="true"
          />
        ))}

        {/* vinhetas para legibilidade do texto */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[hsl(222,47%,6%)]/70 via-transparent to-[hsl(222,47%,6%)]/80" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,hsl(222,47%,6%,0.65)_100%)]" />

        {/* FASE 1 — O PULSO (hero) */}
        <div
          ref={heroRef}
          className="absolute inset-0 z-10 flex items-center justify-center px-4 pt-20"
        >
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-[hsl(222,47%,6%)]/60 px-4 py-2 backdrop-blur-sm">
              <Radar className="h-4 w-4 animate-pulse text-primary" />
              <span className="sonar-label text-primary">{t(h.badge, lang)}</span>
            </div>

            <h1 className="mb-6 font-display text-4xl font-bold text-white md:text-6xl lg:text-7xl [text-shadow:0_2px_24px_rgba(2,8,23,0.85)]">
              <span className="text-primary">{heroH1}</span>
              <span className="sr-only"> — External Attack Surface Management (EASM)</span>
            </h1>

            <p className="mx-auto mb-8 max-w-2xl text-lg text-slate-200 md:text-xl [text-shadow:0_1px_14px_rgba(2,8,23,0.9)]">
              <span className="font-semibold text-white">{secondaryCall}</span>
              <br />
              {t(h.subtitle, lang)}
            </p>

            <div className="mb-12 flex items-center justify-center">
              <Button
                variant="hero"
                size="xl"
                className="cta-ping relative"
                onClick={() => scrollToAnchor("#contact")}
              >
                {t(h.cta, lang)}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-white/10 bg-[hsl(222,47%,8%)]/60 p-4 backdrop-blur-md md:p-6"
                >
                  <div className="font-mono text-2xl font-semibold text-primary md:text-3xl">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FASE 2 — OS ECOS (descoberta) */}
        <SceneOverlay refEl={easmRef} align="left" copy={copy.easm} />
        {/* FASE 3 — CONTATOS HOSTIS (vulnerabilidades) */}
        <SceneOverlay refEl={vulnRef} align="right" copy={copy.vuln} />
        {/* FASE 4 — TRIANGULAÇÃO (análise por IA) */}
        <SceneOverlay refEl={iaRef} align="left" copy={copy.ia} />

        {/* indicador de rolagem */}
        <div
          ref={hintRef}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 animate-bounce"
          role="img"
          aria-label={t(h.scrollIndicator, lang)}
        >
          <div className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/25 p-2">
            <div className="h-2 w-1 animate-pulse rounded-full bg-primary" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */

interface SceneCopy {
  title: string;
  text: string;
  chip: string;
  severity: boolean;
}

const SceneOverlay = ({
  refEl,
  copy,
  align,
}: {
  refEl: React.RefObject<HTMLDivElement>;
  copy: SceneCopy;
  align: "left" | "right";
}) => (
  <div
    ref={refEl}
    className={`pointer-events-none absolute inset-0 z-10 flex items-center px-6 opacity-0 md:px-16 lg:px-24 ${
      align === "right" ? "justify-end" : "justify-start"
    }`}
  >
    <div className="max-w-xl rounded-2xl border border-white/10 bg-[hsl(222,47%,6%)]/55 p-8 backdrop-blur-md md:p-10">
      <h2 className="mb-4 font-display text-3xl font-bold text-white md:text-5xl">
        {copy.title}
      </h2>
      <p className="mb-6 text-base text-slate-300 md:text-lg">{copy.text}</p>
      <span
        className={`sonar-label inline-flex items-center gap-2 rounded-full border px-4 py-2 ${
          copy.severity
            ? "border-[hsl(var(--sev-medium))]/50 bg-[hsl(var(--sev-medium))]/10 text-sev-medium"
            : "border-primary/40 bg-primary/10 text-primary"
        }`}
      >
        {copy.chip}
      </span>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Fallback estático (?motion=reduce): momentos-chave                  */
/* ------------------------------------------------------------------ */

const StaticStory = () => {
  const { lang } = useLanguage();
  const h = translations.hero;
  const copy = useSceneCopy();
  const heroH1 = t(homeSeo.h1, lang);
  const secondaryCall = t(SECONDARY_CALL, lang);

  const sceneCards: { poster: number; content: JSX.Element }[] = [
    {
      poster: 1,
      content: (
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-[hsl(222,47%,6%)]/60 px-4 py-2">
            <Radar className="h-4 w-4 text-primary" />
            <span className="sonar-label text-primary">{t(h.badge, lang)}</span>
          </div>
          <h1 className="mb-6 font-display text-4xl font-bold text-white md:text-6xl">
            <span className="text-primary">{heroH1}</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-slate-300">
            <span className="font-semibold text-white">{secondaryCall}</span>
            <br />
            {t(h.subtitle, lang)}
          </p>
          <Button variant="hero" size="xl" className="cta-ping relative" onClick={() => scrollToAnchor("#contact")}>
            {t(h.cta, lang)}
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      ),
    },
    ...([copy.easm, copy.vuln, copy.ia].map((c, i) => ({
      poster: i + 2,
      content: (
        <div className="max-w-xl rounded-2xl border border-white/10 bg-[hsl(222,47%,6%)]/60 p-8">
          <h2 className="mb-4 font-display text-3xl font-bold text-white md:text-4xl">{c.title}</h2>
          <p className="text-slate-300">{c.text}</p>
        </div>
      ),
    })) as { poster: number; content: JSX.Element }[]),
  ];

  return (
    <section id="hero" className="dark relative z-10">
      {sceneCards.map((scene, i) => (
        <div key={i} className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-24">
          <img
            src={posterSrc(scene.poster)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-[hsl(222,47%,6%)]/60" />
          <div className="relative z-10">{scene.content}</div>
        </div>
      ))}
    </section>
  );
};

export default ScrollCinema;
